import { matchRoutes } from 'react-router-dom';
import { WAKE_EVERY_SCREEN } from '../config/wake/everyScreen';
import { WAKE_LANDING } from '../config/wake/landing';
import { WAKE_MODULES } from '../config/wake/modules';
import { NEIGHBOUR_ROUTE_TABLE, WAKE_NEIGHBOURS, WAKE_PAGES } from '../config/wake/neighbours';
import { WAKE_ROUTES } from '../config/wake/routes';
import { ModuleRegistry } from '../modules/registry';
import type { NavItem } from '../modules/types';
import type { WakeScreenDef } from '../config/wakeKnock';
import type { ModuleId } from '../contexts/ModuleContext';

/**
 * WHAT the keep-warm loop keeps warm (L-0858 lists, L-0868 loop). Pure, so a test can check the set without rendering.
 */

/** Module id (contexts/ModuleContext) -> its module-entry list in config/wake/modules.ts. Absent = nothing on entry. */
export const WAKE_MODULE_KEY: Readonly<Partial<Record<ModuleId, keyof typeof WAKE_MODULES>>> = {
  P11: 'fieldService',
  P10: 'projects',
  P4: 'billing',
  P5: 'support',
  P7: 'fleet',
  P2: 'quoting',
  P1: 'crm',
  P14: 'recruiting',
};

/**
 * Module id -> its landing list in config/wake/landing.ts. Absent = no landing list. The ORDER of this object is the knock
 * order (most staff cold-wait first, 2026-10-12..14), so it does not depend on the order an admin typed enabledModules in.
 */
export const WAKE_LANDING_KEY: Readonly<Partial<Record<ModuleId, keyof typeof WAKE_LANDING>>> = {
  P11: 'fieldService',
  P10: 'projects',
  P4: 'billing',
  P7: 'fleet',
  P1: 'crm',
  P6: 'explorer',
  P14: 'recruiting',
  P15: 'marketing',
  AI_SYSTEMS: 'aiSystems',
  P19: 'workshop',
};

const NONE: WakeScreenDef = { enabled: false, callables: [] };

/** The module-entry list for `moduleId` (null, or a module with no list = nothing). */
export function wakeDefForModule(moduleId: ModuleId | null | undefined): WakeScreenDef {
  const key = moduleId ? WAKE_MODULE_KEY[moduleId] : undefined;
  return key ? WAKE_MODULES[key] : NONE;
}

/** The module ids whose landing list is knocked, in knock order: only modules with a list the user can open. */
export function landingModuleIds(
  moduleIds: readonly ModuleId[],
  hasAccess: (moduleId: ModuleId) => boolean,
): ModuleId[] {
  const mine = new Set(moduleIds);
  return (Object.keys(WAKE_LANDING_KEY) as ModuleId[]).filter((id) => mine.has(id) && hasAccess(id));
}

const on = (def: WakeScreenDef): readonly string[] => (def.enabled ? def.callables : []);

/** Every nav AND sub-nav path of a nav tree (children included), query string dropped. Pure. */
export function navPaths(items: readonly NavItem[]): string[] {
  return items.flatMap((item) => [item.path.split('?')[0], ...navPaths(item.children ?? [])]);
}

/**
 * Everything to keep warm while the user is in `moduleId` (L-0868 stage b): the module's own list (modules.ts) plus the list of
 * every nav and sub-nav page (routes.ts), de-duplicated. A page with no list adds nothing. `navItemsOf` is injectable for tests.
 * Role-gated pages (requireManagerOrAdmin, requiredRoles, ...) are included for every user of the module ON PURPOSE: a knock is a
 * credential-less OPTIONS that runs no handler and reads no data, and the names are already in the shipped bundle. Filtering by
 * role would only save a few knocks, and it would split the completeness check from what is knocked.
 */
export function moduleKeepWarmNames(
  moduleId: ModuleId,
  navItemsOf: (id: ModuleId) => readonly NavItem[] = ModuleRegistry.getNavItems,
): string[] {
  const routes = WAKE_ROUTES as Readonly<Record<string, WakeScreenDef>>;
  const pages = navPaths(navItemsOf(moduleId)).flatMap((p) => (routes[p] ? on(routes[p]) : []));
  return Array.from(new Set([...on(wakeDefForModule(moduleId)), ...pages]));
}

const ROUTE_TABLE = NEIGHBOUR_ROUTE_TABLE.map((path) => ({ path }));

/** Route pattern -> its neighbour lists, one per module that owns the linked pages (key '<pattern> => <urlPrefix>'). */
const NEIGHBOURS_BY_ROUTE: ReadonlyMap<string, ReadonlyArray<{ prefix: string; def: WakeScreenDef }>> = (() => {
  const out = new Map<string, Array<{ prefix: string; def: WakeScreenDef }>>();
  Object.entries(WAKE_NEIGHBOURS as Readonly<Record<string, WakeScreenDef>>).forEach(([key, def]) => {
    const parsed = parseWakeKey(key);
    if (parsed && 'prefix' in parsed) out.set(parsed.route, [...(out.get(parsed.route) ?? []), { prefix: parsed.prefix, def }]);
  });
  return out;
})();

/**
 * The keys of the generated lists in config/wake/neighbours.ts, parsed in one place: a neighbour list is
 * '<route pattern> => <urlPrefix>', a page chunk is '<route pattern> #<n>'. Anything else is null.
 */
export function parseWakeKey(key: string): { route: string; prefix: string } | { route: string; chunk: number } | null {
  const near = key.match(/^(\/\S*) => (\/\S*)$/);
  if (near) return { route: near[1], prefix: near[2] };
  const page = key.match(/^(\/\S*) #(\d+)$/);
  return page ? { route: page[1], chunk: Number(page[2]) } : null;
}

/** Route pattern -> the page's own lists ('<pattern> #1', '#2', ...). */
const PAGE_LISTS: ReadonlyMap<string, readonly WakeScreenDef[]> = (() => {
  const out = new Map<string, WakeScreenDef[]>();
  Object.entries(WAKE_PAGES as Readonly<Record<string, WakeScreenDef>>).forEach(([key, def]) => {
    const parsed = parseWakeKey(key);
    if (parsed && 'chunk' in parsed) out.set(parsed.route, [...(out.get(parsed.route) ?? []), def]);
  });
  return out;
})();

/** The route pattern `pathname` matches (react-router ranking over the whole table), or undefined. */
function routeOf(pathname: string | null | undefined): string | undefined {
  return pathname ? matchRoutes(ROUTE_TABLE, pathname)?.[0]?.route.path : undefined;
}

const moduleOfRegistry = (path: string): ModuleId | undefined => ModuleRegistry.getByUrlPrefix(path)?.id;

/**
 * The page's OWN calls (L-0868 stage d): every name the screen at `pathname` reaches that went cold, uncapped, so its
 * buttons are warm while the user is on it (a rarely used button is exactly the one that finds its server cold). Only if
 * the user can open the module that OWNS the matched route (checked here too, not only through the URL's current module).
 */
export function pageNames(
  pathname: string | null | undefined,
  hasAccess: (moduleId: ModuleId) => boolean,
  moduleOfPath: (path: string) => ModuleId | undefined = moduleOfRegistry,
): string[] {
  const pattern = routeOf(pathname);
  const owner = pattern ? moduleOfPath(pattern) : undefined;
  if (!pattern || owner === undefined || !hasAccess(owner)) return [];
  return Array.from(new Set((PAGE_LISTS.get(pattern) ?? []).flatMap((def) => on(def))));
}

/**
 * The pages one click from `pathname` (L-0868 stage c): for the route it matches (best match by react-router's own ranking over
 * the whole route table), the lists of the linked pages, each only if the user can open the module that owns them (a ticket
 * that links into Billing does not wake Billing's servers for someone without Billing). No match = nothing.
 */
export function neighbourNames(
  pathname: string | null | undefined,
  hasAccess: (moduleId: ModuleId) => boolean,
  moduleOfPrefix: (prefix: string) => ModuleId | undefined = moduleOfRegistry,
): string[] {
  const pattern = routeOf(pathname);
  const lists = pattern ? NEIGHBOURS_BY_ROUTE.get(pattern) ?? [] : [];
  const allowed = lists.filter(({ prefix }) => {
    const id = moduleOfPrefix(prefix);
    return id !== undefined && hasAccess(id);
  });
  return Array.from(new Set(allowed.flatMap(({ def }) => on(def))));
}

/**
 * The de-duplicated set to keep warm right now: the current page's own calls (stage d), the current module's list and every
 * nav/sub-nav page's list (the user is there), this page's one-click neighbours (stage c), every accessible module's landing
 * list in WAKE_LANDING_KEY order, and the calls every screen makes (config/wake/everyScreen.ts). `hasAccess` is REQUIRED (no allow-all default): the current
 * module comes from the URL, so a user without the module who follows a link must not wake its servers.
 */
export function keepWarmNames(
  moduleIds: readonly ModuleId[],
  currentModule: ModuleId | null | undefined,
  hasAccess: (moduleId: ModuleId) => boolean,
  pathname?: string | null,
): string[] {
  const inModule = currentModule && hasAccess(currentModule) ? currentModule : null;
  const current = inModule ? moduleKeepWarmNames(inModule) : [];
  // This page's own calls, then the pages one click away: only on a page in a module the user can open, and each neighbour
  // list only for a module they can open.
  const page = inModule ? pageNames(pathname, hasAccess) : [];
  const near = inModule ? neighbourNames(pathname, hasAccess) : [];
  const landing = landingModuleIds(moduleIds, hasAccess).flatMap((id) => {
    const key = WAKE_LANDING_KEY[id];
    return key ? on(WAKE_LANDING[key]) : [];
  });
  const everyScreen = Object.values(WAKE_EVERY_SCREEN).flatMap((def) => on(def));
  return Array.from(new Set([...page, ...current, ...near, ...landing, ...everyScreen]));
}

/**
 * Hard cap on the hidden-tab set. Uncapped, a module's entry + landing + page lists reach ~38 names; at one knock per name every 9-11 minutes
 * a tab left hidden for the full 2 hours is ~450 knocks, and a whole shift of such tabs bounds the worst-case month near the paid tier. 12 =
 * one wake chunk (WAKE_MAX_NAMES), which keeps the worst case well under $10 a month (ORCH-102 ruling on #8706 r1).
 */
export const HIDDEN_KEEP_WARM_MAX_NAMES = 12;

/**
 * The SMALL core set kept warm while the tab is HIDDEN (smart-knock): what the user clicks first on coming back, not everything the visible
 * loop covers. The current page's own calls (its buttons), the current module's entry list and that module's landing list; nothing for a user
 * without the module. A subset of keepWarmNames by construction (a test pins it), so it can never warm a name the visible loop would not.
 */
export function hiddenKeepWarmNames(
  currentModule: ModuleId | null | undefined,
  hasAccess: (moduleId: ModuleId) => boolean,
  pathname?: string | null,
): string[] {
  if (!currentModule || !hasAccess(currentModule)) return [];
  const landingKey = WAKE_LANDING_KEY[currentModule];
  const landing = landingKey ? on(WAKE_LANDING[landingKey]) : [];
  // Page calls first (the buttons in front of the user), then the module's own list, then its landing list; cut at the cap.
  return Array.from(new Set([...pageNames(pathname, hasAccess), ...on(wakeDefForModule(currentModule)), ...landing])).slice(0, HIDDEN_KEEP_WARM_MAX_NAMES);
}
